import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Calendar as CalendarIcon,
    LogOut,
    Plus,
    Trash2,
    Loader2,
    RefreshCcw,
    Zap,
    Settings as SettingsIcon,
    Save,
    Pencil,
    Sun,
    Moon,
    X,
    Download,
    Mail,
    Users,
    ClipboardList,
    BellRing,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminApi,
    setAdminPassword,
    clearAdminPassword,
    getAdminPassword,
    api,
} from "@/lib/api";
import { Logo, SiteTag } from "@/components/Logo";
import { useSiteInfo } from "@/hooks/useSiteInfo";

export default function Admin() {
    const [authed, setAuthed] = useState(Boolean(getAdminPassword()));
    return authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
    ) : (
        <Login onAuthed={() => setAuthed(true)} />
    );
}

function Login({ onAuthed }) {
    const site = useSiteInfo();
    const [username, setUsername] = useState("");
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/admin/login", { username, password: pw });
            setAdminPassword(pw);
            onAuthed();
        } catch {
            toast.error("Ongeldige inloggegevens");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-12">
            <aside className="lg:col-span-5 bg-[#0A1F44] text-white p-10 md:p-16 flex flex-col">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
                    data-testid="back-home"
                >
                    ← Terug naar website
                </Link>
                <div className="mt-12 flex flex-col gap-4 items-start">
                    <Logo tone="light" subtitle="ADMIN" />
                    <SiteTag name={site.name} city={site.city} tone="light" />
                </div>
                <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter mt-12 leading-[1.05]">
                    Beheer voor
                    <br />
                    <span className="text-[#ED6A14]">het team.</span>
                </h1>
                <p className="text-white/60 mt-6 max-w-sm">
                    Publiceer wasdagen, bekijk boekingen en houd de planning op
                    {" "}
                    {site.name} op schema.
                </p>
            </aside>
            <main className="lg:col-span-7 p-10 md:p-16 flex items-center">
                <form
                    onSubmit={submit}
                    className="w-full max-w-md"
                    data-testid="admin-login-form"
                >
                    <p className="text-xs uppercase tracking-widest text-[#555]">
                        Aanmelden
                    </p>
                    <h2 className="font-display font-bold text-3xl tracking-tight mt-2 text-[#0A1F44]">
                        Admin login
                    </h2>
                    <div className="mt-8">
                        <label className="text-xs uppercase tracking-widest text-[#555]">
                            Gebruikersnaam
                        </label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="naam@cargidesgroup"
                            className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                            data-testid="admin-username-input"
                            autoFocus
                            autoComplete="username"
                        />
                    </div>
                    <div className="mt-8">
                        <label className="text-xs uppercase tracking-widest text-[#555]">
                            Wachtwoord
                        </label>
                        <input
                            type="password"
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder="••••••••"
                            className="w-full border-b-2 border-[#e5e5e5] bg-transparent py-4 text-xl focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                            data-testid="admin-password-input"
                            autoComplete="current-password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading || !username || !pw}
                        className="mt-10 inline-flex items-center gap-2 bg-[#ED6A14] text-white px-7 py-3.5 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                        data-testid="admin-login-btn"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin" size={16} />
                        ) : null}{" "}
                        Naar dashboard
                    </button>
                </form>
            </main>
        </div>
    );
}

function Dashboard({ onLogout }) {
    const site = useSiteInfo();
    const [days, setDays] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [marketing, setMarketing] = useState([]);
    const [newDate, setNewDate] = useState("");
    const [newWorkers, setNewWorkers] = useState(2);
    const [newSlotMode, setNewSlotMode] = useState("full");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [quickLoading, setQuickLoading] = useState(false);
    const [settings, setSettings] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [editing, setEditing] = useState(null); // booking object or null
    const [editingDay, setEditingDay] = useState(null); // day object or null
    const [newMarketingEmail, setNewMarketingEmail] = useState("");
    const [sending7day, setSending7day] = useState(false);
    const [sendingRebook, setSendingRebook] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [d, b, s, m] = await Promise.all([
                adminApi.get("/admin/days"),
                adminApi.get("/admin/bookings"),
                adminApi.get("/admin/settings"),
                adminApi.get("/admin/marketing-recipients"),
            ]);
            setDays(d.data);
            setBookings(b.data);
            setSettings(s.data);
            setMarketing(m.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearAdminPassword();
                onLogout();
                return;
            }
            toast.error("Laden mislukt");
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        if (!settings) return;
        setSavingSettings(true);
        try {
            const res = await adminApi.put("/admin/settings", {
                site_name: settings.site_name,
                site_city: settings.site_city,
                price_exterior: parseFloat(settings.price_exterior),
                price_interior_exterior: parseFloat(
                    settings.price_interior_exterior,
                ),
                duration_exterior_min: parseInt(
                    settings.duration_exterior_min,
                    10,
                ),
                duration_interior_exterior_min: parseInt(
                    settings.duration_interior_exterior_min,
                    10,
                ),
                booking_cutoff_hours:
                    parseInt(settings.booking_cutoff_hours, 10) || 0,
            });
            setSettings(res.data);
            toast.success("Park-instellingen opgeslagen");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Opslaan mislukt",
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const addMarketing = async (e) => {
        e.preventDefault();
        const email = newMarketingEmail.trim().toLowerCase();
        if (!email) return;
        try {
            await adminApi.post("/admin/marketing-recipients", { email });
            setNewMarketingEmail("");
            toast.success("E-mailadres toegevoegd");
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Toevoegen mislukt",
            );
        }
    };

    const deleteMarketing = async (id) => {
        if (!window.confirm("Dit e-mailadres verwijderen?")) return;
        try {
            await adminApi.delete(`/admin/marketing-recipients/${id}`);
            toast.success("Verwijderd");
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Verwijderen mislukt",
            );
        }
    };

    const send7dayReminders = async () => {
        if (sending7day) return;
        setSending7day(true);
        try {
            const res = await adminApi.post("/admin/reminders/7day/send-now");
            const d = res.data || {};
            if (d.reason === "no_wash_day") {
                toast.info(`Geen wasdag over 7 dagen (${d.date}).`);
            } else if (d.reason === "already_sent") {
                toast.info("Herinneringen al verstuurd voor deze dag.");
            } else if (d.reason === "no_api_key") {
                toast.error("RESEND_API_KEY ontbreekt.");
            } else {
                toast.success(
                    `Verstuurd: ${d.sent_a} klant(en) + ${d.sent_b} prospect(s) voor ${d.date}`,
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Mislukt",
            );
        } finally {
            setSending7day(false);
        }
    };

    const sendRebookReminders = async () => {
        if (sendingRebook) return;
        setSendingRebook(true);
        try {
            const res = await adminApi.post("/admin/rebook-reminders/send-now");
            const d = res.data || {};
            toast.success(
                `Rebook-mails: ${d.sent || 0} verstuurd voor ${d.date || "—"}`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Mislukt",
            );
        } finally {
            setSendingRebook(false);
        }
    };

    const deleteBooking = async (b) => {
        const ok = window.confirm(
            `Boeking voor ${b.license_plate} op ${b.date} verwijderen? Dit kan niet ongedaan worden gemaakt.`,
        );
        if (!ok) return;
        try {
            await adminApi.delete(`/admin/bookings/${b.id}`);
            toast.success("Boeking verwijderd");
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Verwijderen mislukt",
            );
        }
    };

    const grouped = useMemo(() => groupBookings(bookings), [bookings]);

    const exportDay = async (date) => {
        try {
            const res = await adminApi.get(
                `/admin/bookings/export?date=${date}`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `carwash-${date}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`CSV gedownload voor ${date}`);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "CSV-export mislukt",
            );
        }
    };

    const [sendingCrewMail, setSendingCrewMail] = useState(false);
    const sendCrewMailNow = async () => {
        if (sendingCrewMail) return;
        setSendingCrewMail(true);
        try {
            const res = await adminApi.post("/admin/crew-csv/send-now");
            const data = res.data || {};
            if (data.sent) {
                toast.success(
                    `Mail verstuurd naar ${data.to} (${data.bookings} boeking${
                        data.bookings === 1 ? "" : "en"
                    } voor ${data.date})`,
                );
            } else {
                const reasons = {
                    no_api_key: "RESEND_API_KEY ontbreekt.",
                    no_crew_email: "CREW_EMAIL ontbreekt in .env.",
                    no_upcoming_day: "Geen aankomende wasdag gevonden.",
                    send_failed: "Versturen mislukt: " + (data.error || ""),
                };
                toast.error(reasons[data.reason] || "Mail niet verstuurd.");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Crew-mail mislukt",
            );
        } finally {
            setSendingCrewMail(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line
    }, []);

    const addDay = async (e) => {
        e.preventDefault();
        if (!newDate) return;
        setSubmitting(true);
        try {
            await adminApi.post("/admin/days", {
                date: newDate,
                workers_count: newWorkers,
                slot_mode: newSlotMode,
            });
            setNewDate("");
            toast.success("Wasdag gepubliceerd");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kon dag niet toevoegen");
        } finally {
            setSubmitting(false);
        }
    };

    const deleteDay = async (id) => {
        if (!window.confirm("Deze wasdag verwijderen?")) return;
        try {
            await adminApi.delete(`/admin/days/${id}`);
            toast.success("Wasdag verwijderd");
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Kon dag niet verwijderen",
            );
        }
    };

    const saveDayEdit = async (day, patch) => {
        try {
            await adminApi.patch(`/admin/days/${day.id}`, patch);
            toast.success("Dag bijgewerkt");
            setEditingDay(null);
            refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Bijwerken mislukt",
            );
        }
    };

    const quickAddDates = async (dates, label) => {
        if (dates.length === 0) return;
        setQuickLoading(true);
        try {
            const res = await adminApi.post("/admin/days/bulk", {
                dates,
                workers_count: newWorkers,
                slot_mode: newSlotMode,
            });
            toast.success(`${label}: ${res.data.length} dag(en) gepubliceerd`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kon dagen niet toevoegen");
        } finally {
            setQuickLoading(false);
        }
    };

    const addTomorrow = () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        quickAddDates([d.toISOString().slice(0, 10)], "Morgen");
    };

    const addNextWeekdays = (count, label) => {
        const dates = [];
        const d = new Date();
        d.setDate(d.getDate() + 1);
        while (dates.length < count) {
            const dow = d.getDay(); // 0=Sun, 6=Sat
            if (dow !== 0 && dow !== 6) {
                dates.push(d.toISOString().slice(0, 10));
            }
            d.setDate(d.getDate() + 1);
        }
        quickAddDates(dates, label);
    };

    const logout = () => {
        clearAdminPassword();
        onLogout();
    };

    const confirmedCount = bookings.filter(
        (b) => b.status === "confirmed",
    ).length;
    const revenue = bookings
        .filter((b) => b.status === "confirmed")
        .reduce((s, b) => s + (b.amount_eur || 0), 0);

    return (
        <div className="min-h-screen grid lg:grid-cols-12">
            <aside className="lg:col-span-2 bg-[#0A1F44] text-white p-6 md:p-8 flex lg:flex-col justify-between">
                <div>
                    <Link
                        to="/"
                        className="block"
                        data-testid="brand-link"
                    >
                        <Logo tone="light" subtitle="ADMIN" />
                    </Link>
                    <nav className="mt-10 hidden lg:flex flex-col gap-1 text-sm">
                        <span className="px-3 py-2 bg-[#ED6A14] text-white font-semibold">
                            Dashboard
                        </span>
                    </nav>
                </div>
                <button
                    onClick={logout}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
                    data-testid="admin-logout-btn"
                >
                    <LogOut size={14} /> Afmelden
                </button>
            </aside>

            <main className="lg:col-span-10 p-6 md:p-10 lg:p-14">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="mb-3">
                            <SiteTag name={site.name} city={site.city} />
                        </div>
                        <p className="text-xs uppercase tracking-widest text-[#555]">
                            Overzicht
                        </p>
                        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter mt-2 text-[#0A1F44]">
                            Dashboard
                        </h1>
                    </div>
                    <button
                        onClick={refresh}
                        className="inline-flex items-center gap-2 border border-[#e5e5e5] px-4 py-2 text-sm hover:border-[#0A1F44] transition-colors text-[#0A1F44]"
                        data-testid="admin-refresh-btn"
                    >
                        <RefreshCcw size={14} /> Vernieuwen
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e5e5e5] border border-[#e5e5e5] mb-12">
                    <Kpi
                        label="Actieve wasdagen"
                        value={days.length}
                        testId="kpi-days"
                    />
                    <Kpi
                        label="Bevestigde boekingen"
                        value={confirmedCount}
                        testId="kpi-confirmed"
                    />
                    <Kpi
                        label="Omzet (betaald)"
                        value={`€${revenue.toFixed(2).replace(".", ",")}`}
                        testId="kpi-revenue"
                    />
                </div>

                {/* Park settings */}
                <section className="mb-12" data-testid="park-settings-section">
                    <div className="flex items-center gap-3 mb-2">
                        <SettingsIcon
                            size={20}
                            className="text-[#ED6A14]"
                        />
                        <h2 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                            Park-instellingen
                        </h2>
                    </div>
                    <p className="text-sm text-[#555] mb-6">
                        Pas hier de naam, stad en prijzen aan. Wijzigingen zijn
                        meteen zichtbaar op de publieke site — handig om dit
                        platform te dupliceren voor een ander bedrijvenpark.
                    </p>
                    {settings ? (
                        <form
                            onSubmit={saveSettings}
                            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 max-w-3xl border border-[#e5e5e5] p-6"
                            data-testid="park-settings-form"
                        >
                            <Field
                                label="Park-naam"
                                value={settings.site_name}
                                onChange={(v) =>
                                    setSettings({ ...settings, site_name: v })
                                }
                                testId="setting-name"
                            />
                            <Field
                                label="Stad"
                                value={settings.site_city}
                                onChange={(v) =>
                                    setSettings({ ...settings, site_city: v })
                                }
                                testId="setting-city"
                            />
                            <Field
                                label="Prijs Exterieur (€, incl. btw)"
                                type="number"
                                step="0.01"
                                value={settings.price_exterior}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        price_exterior: v,
                                    })
                                }
                                testId="setting-price-exterior"
                            />
                            <Field
                                label="Prijs Interieur & Exterieur (€, incl. btw)"
                                type="number"
                                step="0.01"
                                value={settings.price_interior_exterior}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        price_interior_exterior: v,
                                    })
                                }
                                testId="setting-price-full"
                            />
                            <Field
                                label="Duur Exterieur (minuten, 5–240)"
                                type="number"
                                step="5"
                                min="5"
                                max="240"
                                value={settings.duration_exterior_min}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        duration_exterior_min: v,
                                    })
                                }
                                testId="setting-duration-exterior"
                            />
                            <Field
                                label="Duur Interieur & Exterieur (minuten, 5–240)"
                                type="number"
                                step="5"
                                min="5"
                                max="240"
                                value={settings.duration_interior_exterior_min}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        duration_interior_exterior_min: v,
                                    })
                                }
                                testId="setting-duration-full"
                            />
                            <div className="md:col-span-2">
                                <Field
                                    label="Boeken kan tot (uren vóór 08:00 op de wasdag)"
                                    type="number"
                                    step="1"
                                    min="0"
                                    max="720"
                                    value={settings.booking_cutoff_hours}
                                    onChange={(v) =>
                                        setSettings({
                                            ...settings,
                                            booking_cutoff_hours: v,
                                        })
                                    }
                                    testId="setting-booking-cutoff"
                                />
                                <p
                                    className="text-xs text-[#555] mt-2"
                                    data-testid="setting-booking-cutoff-hint"
                                >
                                    {cutoffHint(settings.booking_cutoff_hours)}
                                </p>
                            </div>
                            <div className="md:col-span-2 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={savingSettings}
                                    className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                                    data-testid="save-settings-btn"
                                >
                                    {savingSettings ? (
                                        <Loader2
                                            className="animate-spin"
                                            size={16}
                                        />
                                    ) : (
                                        <Save size={16} />
                                    )}{" "}
                                    Instellingen opslaan
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="text-[#555] inline-flex items-center gap-2">
                            <Loader2 className="animate-spin" size={16} /> Laden…
                        </div>
                    )}
                </section>

                <section className="mb-12">
                    <h2 className="font-display font-bold text-2xl tracking-tight mb-2 text-[#0A1F44]">
                        Wasdag(en) publiceren voor {site.name}
                    </h2>
                    <p className="text-sm text-[#555] mb-6">
                        Open snel boekingsdagen voor het bedrijvenpark. Bestaande
                        dagen worden automatisch overgeslagen.
                    </p>

                    {/* Capacity picker: shared by quick-add + specific date */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 max-w-2xl">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                                Poetsers per dag (1–4)
                            </label>
                            <select
                                value={newWorkers}
                                onChange={(e) =>
                                    setNewWorkers(parseInt(e.target.value, 10))
                                }
                                className="w-full border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                                data-testid="new-workers-select"
                            >
                                {[1, 2, 3, 4].map((n) => (
                                    <option key={n} value={n}>
                                        {n} poetser{n === 1 ? "" : "s"} · {n * 480} min/dag
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                                Dagdeel
                            </label>
                            <select
                                value={newSlotMode}
                                onChange={(e) => setNewSlotMode(e.target.value)}
                                className="w-full border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                                data-testid="new-slot-mode-select"
                            >
                                <option value="full">Volledige dag (VM + NM)</option>
                                <option value="morning">Enkel voormiddag</option>
                                <option value="afternoon">Enkel namiddag</option>
                            </select>
                        </div>
                    </div>

                    {/* Quick add buttons */}
                    <div className="flex flex-wrap gap-3 mb-8">
                        <button
                            onClick={addTomorrow}
                            disabled={quickLoading}
                            className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-50"
                            data-testid="quick-tomorrow-btn"
                        >
                            <Zap size={14} /> Morgen
                        </button>
                        <button
                            onClick={() =>
                                addNextWeekdays(5, "Komende 5 werkdagen")
                            }
                            disabled={quickLoading}
                            className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-50"
                            data-testid="quick-week-btn"
                        >
                            <Zap size={14} /> Komende 5 werkdagen
                        </button>
                        <button
                            onClick={() =>
                                addNextWeekdays(10, "Komende 2 weken")
                            }
                            disabled={quickLoading}
                            className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-5 py-2.5 font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-50"
                            data-testid="quick-2weeks-btn"
                        >
                            <Zap size={14} /> Komende 2 weken werkdagen
                        </button>
                        {quickLoading && (
                            <span className="inline-flex items-center gap-2 text-[#555] text-sm">
                                <Loader2
                                    className="animate-spin"
                                    size={14}
                                />{" "}
                                Toevoegen…
                            </span>
                        )}
                    </div>

                    {/* Manual single date add */}
                    <form
                        onSubmit={addDay}
                        className="flex flex-wrap items-end gap-4 pt-6 border-t border-[#e5e5e5]"
                        data-testid="add-day-form"
                    >
                        <div>
                            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                                Of kies een specifieke datum
                            </label>
                            <input
                                type="date"
                                value={newDate}
                                onChange={(e) => setNewDate(e.target.value)}
                                min={new Date().toISOString().slice(0, 10)}
                                className="border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                                data-testid="new-day-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting || !newDate}
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                            data-testid="add-day-btn"
                        >
                            <Plus size={16} /> Publiceren
                        </button>
                    </form>
                </section>

                <section className="mb-16">
                    <h2 className="font-display font-bold text-2xl tracking-tight mb-6 text-[#0A1F44]">
                        Gepubliceerde dagen
                    </h2>
                    {loading ? (
                        <div className="text-[#555] inline-flex items-center gap-2">
                            <Loader2 className="animate-spin" size={16} />{" "}
                            Laden…
                        </div>
                    ) : days.length === 0 ? (
                        <p className="text-[#555]" data-testid="no-days-msg">
                            Nog geen wasdagen. Publiceer er hierboven een.
                        </p>
                    ) : (
                        <div className="border border-[#e5e5e5] divide-y divide-[#e5e5e5]">
                            {days.map((d) => {
                                const modeLabel =
                                    d.slot_mode === "morning"
                                        ? "Enkel VM"
                                        : d.slot_mode === "afternoon"
                                        ? "Enkel NM"
                                        : "VM + NM";
                                return (
                                    <div
                                        key={d.id}
                                        className="flex items-center justify-between px-5 py-4"
                                        data-testid={`day-row-${d.date}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <CalendarIcon
                                                size={18}
                                                className="text-[#ED6A14]"
                                            />
                                            <div>
                                                <p className="font-semibold text-[#0A1F44]">
                                                    {new Date(d.date).toLocaleDateString(
                                                        "nl-BE",
                                                        {
                                                            weekday: "short",
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        },
                                                    )}
                                                </p>
                                                <p className="text-xs text-[#555] mt-0.5 font-mono-ui">
                                                    {d.workers_count} poetser
                                                    {d.workers_count === 1 ? "" : "s"} ·{" "}
                                                    {modeLabel} · VM{" "}
                                                    {d.morning_active
                                                        ? `${d.morning_remaining_min}/${d.morning_total_min}min`
                                                        : "uit"}
                                                    {" · "}
                                                    NM{" "}
                                                    {d.afternoon_active
                                                        ? `${d.afternoon_remaining_min}/${d.afternoon_total_min}min`
                                                        : "uit"}
                                                </p>
                                                {!d.booking_open && (
                                                    <span
                                                        className="inline-block mt-1 text-[10px] uppercase tracking-widest font-semibold text-[#D92D20]"
                                                        data-testid={`day-closed-${d.date}`}
                                                    >
                                                        Boeken afgesloten
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => setEditingDay(d)}
                                                className="text-[#555] hover:text-[#ED6A14] transition-colors p-2"
                                                data-testid={`edit-day-${d.date}`}
                                                title="Poetsers / dagdeel bewerken"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => deleteDay(d.id)}
                                                className="text-[#555] hover:text-[#D92D20] transition-colors p-2"
                                                data-testid={`delete-day-${d.date}`}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                <section data-testid="bookings-section">
                    <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                        <h2 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                            Boekingen per dag
                        </h2>
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-[#555]">
                                {bookings.length} boeking
                                {bookings.length !== 1 ? "en" : ""} totaal · gesorteerd
                                op datum &amp; tijdslot
                            </span>
                            <button
                                onClick={send7dayReminders}
                                disabled={sending7day}
                                className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] px-4 py-2 text-sm font-semibold hover:bg-[#0A1F44] hover:text-white transition-colors disabled:opacity-60"
                                data-testid="send-7day-btn"
                                title="Verstuur 7-daagse herinneringen (klanten + prospects) voor de wasdag over 7 dagen. Loopt automatisch elke ochtend om 09:00."
                            >
                                {sending7day ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <BellRing size={14} />
                                )}
                                7-dagen herinnering
                            </button>
                            <button
                                onClick={sendRebookReminders}
                                disabled={sendingRebook}
                                className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] px-4 py-2 text-sm font-semibold hover:bg-[#0A1F44] hover:text-white transition-colors disabled:opacity-60"
                                data-testid="send-rebook-btn"
                                title="Verstuur rebook-herinneringen (bedrijven, 14 dagen geleden). Loopt automatisch elke ochtend om 10:00."
                            >
                                {sendingRebook ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <BellRing size={14} />
                                )}
                                Rebook-mails
                            </button>
                            <button
                                onClick={sendCrewMailNow}
                                disabled={sendingCrewMail}
                                className="inline-flex items-center gap-2 border-2 border-[#ED6A14] text-[#ED6A14] px-4 py-2 text-sm font-semibold hover:bg-[#ED6A14] hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="send-crew-mail-now-btn"
                                title="Stuur de CSV van de volgende wasdag nu naar de wasploeg (loopt automatisch elke avond om 20:00)"
                            >
                                {sendingCrewMail ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Mail size={14} />
                                )}
                                Mail wasploeg nu
                            </button>
                        </div>
                    </div>
                    {bookings.length === 0 ? (
                        <p
                            className="text-[#555]"
                            data-testid="no-bookings-msg"
                        >
                            Nog geen boekingen.
                        </p>
                    ) : (
                        <div className="space-y-10">
                            {grouped.map((day) => (
                                <div
                                    key={day.date}
                                    data-testid={`day-group-${day.date}`}
                                >
                                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                                        <div className="flex items-center gap-3">
                                            <CalendarIcon
                                                size={18}
                                                className="text-[#ED6A14]"
                                            />
                                            <h3 className="font-display font-bold text-xl tracking-tight text-[#0A1F44]">
                                                {formatDayHeading(day.date)}
                                            </h3>
                                            <span className="text-xs text-[#555] font-mono-ui">
                                                {day.total} boeking
                                                {day.total !== 1 ? "en" : ""}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => exportDay(day.date)}
                                            className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] px-4 py-2 text-sm font-semibold hover:bg-[#0A1F44] hover:text-white transition-colors"
                                            data-testid={`export-csv-${day.date}`}
                                            title="Download CSV voor de wasploeg"
                                        >
                                            <Download size={14} /> CSV
                                        </button>
                                    </div>
                                    <div className="space-y-6">
                                        {day.morning.length > 0 && (
                                            <SlotSection
                                                slotKey="morning"
                                                bookings={day.morning}
                                                onEdit={setEditing}
                                                onDelete={deleteBooking}
                                            />
                                        )}
                                        {day.afternoon.length > 0 && (
                                            <SlotSection
                                                slotKey="afternoon"
                                                bookings={day.afternoon}
                                                onEdit={setEditing}
                                                onDelete={deleteBooking}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Marketing / reminder mailing list */}
                <section className="mt-16" data-testid="marketing-section">
                    <div className="flex items-center gap-3 mb-2">
                        <Users size={20} className="text-[#ED6A14]" />
                        <h2 className="font-display font-bold text-2xl tracking-tight text-[#0A1F44]">
                            Herinnerings-mailinglijst
                        </h2>
                    </div>
                    <p className="text-sm text-[#555] mb-6 max-w-3xl">
                        Deze adressen krijgen elke week 7 dagen op voorhand een
                        mail met de aankomende wasdag &amp; vrije capaciteit. Ook
                        historische klanten worden automatisch mee opgenomen — je
                        hoeft die dus niet toe te voegen.
                    </p>
                    <form
                        onSubmit={addMarketing}
                        className="flex flex-wrap items-end gap-3 mb-6"
                        data-testid="marketing-add-form"
                    >
                        <div className="flex-1 min-w-[240px]">
                            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                                E-mailadres toevoegen
                            </label>
                            <input
                                type="email"
                                value={newMarketingEmail}
                                onChange={(e) =>
                                    setNewMarketingEmail(e.target.value)
                                }
                                placeholder="naam@bedrijf.be"
                                className="w-full border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                                data-testid="marketing-email-input"
                            />
                        </div>
                        <button
                            type="submit"
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-5 py-3 font-semibold hover:bg-[#C9550C] transition-colors"
                            data-testid="marketing-add-btn"
                        >
                            <Plus size={16} /> Toevoegen
                        </button>
                    </form>
                    {marketing.length === 0 ? (
                        <p className="text-sm text-[#555]">
                            Nog geen adressen. Voeg er hierboven toe.
                        </p>
                    ) : (
                        <div className="border border-[#e5e5e5] divide-y divide-[#e5e5e5] max-w-3xl">
                            {marketing.map((m) => (
                                <div
                                    key={m.id}
                                    className="flex items-center justify-between px-5 py-3"
                                    data-testid={`marketing-row-${m.email}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <ClipboardList
                                            size={16}
                                            className="text-[#555]"
                                        />
                                        <span className="text-[#0A1F44] text-sm">
                                            {m.email}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => deleteMarketing(m.id)}
                                        className="text-[#555] hover:text-[#D92D20] transition-colors p-2"
                                        data-testid={`marketing-delete-${m.email}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
            {editing && (
                <EditBookingModal
                    booking={editing}
                    days={days}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        refresh();
                    }}
                />
            )}
            {editingDay && (
                <EditDayModal
                    day={editingDay}
                    onClose={() => setEditingDay(null)}
                    onSave={(patch) => saveDayEdit(editingDay, patch)}
                />
            )}
        </div>
    );
}

function cutoffHint(hours) {
    const h = parseInt(hours, 10) || 0;
    if (h === 0) return "0 = klanten kunnen boeken tot de wasdag om 08:00 start.";
    const d = new Date(2000, 0, 10, 8, 0);
    d.setHours(d.getHours() - h);
    const days = Math.round(
        (new Date(2000, 0, 10) - new Date(2000, d.getMonth(), d.getDate())) /
            86400000,
    );
    const time = d.toTimeString().slice(0, 5);
    const when =
        days === 0
            ? "de wasdag zelf"
            : days === 1
            ? "de dag ervoor"
            : `${days} dagen ervoor`;
    return `Boekingen sluiten ${h} uur vóór aanvang: ${when} om ${time}. De dag verdwijnt dan uit het boekingsformulier.`;
}

function SlotSection({ slotKey, bookings, onEdit, onDelete }) {
    const isMorning = slotKey === "morning";
    const Icon = isMorning ? Sun : Moon;
    const label = isMorning
        ? "Voormiddag · 08:00 – 12:00"
        : "Namiddag · 13:00 – 17:00";
    // Use the actual snapshotted duration_min from each booking (falls back to
    // the legacy service-duration helper for very old rows without the field).
    const used = bookings.reduce(
        (s, b) => s + (b.duration_min || serviceDuration(b.service_type)),
        0,
    );
    return (
        <div className="border border-[#e5e5e5]">
            <div
                className={`flex items-center justify-between px-5 py-3 border-b border-[#e5e5e5] ${
                    isMorning ? "bg-[#FFF7F1]" : "bg-[#F2F4F8]"
                }`}
            >
                <div className="flex items-center gap-2.5">
                    <Icon
                        size={16}
                        className={isMorning ? "text-[#ED6A14]" : "text-[#0A1F44]"}
                    />
                    <span className="font-semibold text-[#0A1F44]">
                        {label}
                    </span>
                </div>
                <span className="text-xs text-[#555] font-mono-ui">
                    {bookings.length} wagen{bookings.length !== 1 ? "s" : ""} ·{" "}
                    {used} min gebruikt
                </span>
            </div>
            <div className="overflow-x-auto">
                <table
                    className="w-full text-sm"
                    data-testid={`bookings-table-${slotKey}`}
                >
                    <thead className="bg-[#fcfcfc] text-[#555] uppercase text-[10px] tracking-widest">
                        <tr>
                            <th className="text-left px-4 py-2">Plaat</th>
                            <th className="text-left px-4 py-2">Klant</th>
                            <th className="text-left px-4 py-2">Dienst</th>
                            <th className="text-left px-4 py-2">Btw-nr</th>
                            <th className="text-right px-4 py-2">Bedrag</th>
                            <th className="text-right px-4 py-2">Acties</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bookings.map((b) => (
                            <tr
                                key={b.id}
                                className="border-t border-[#eaeaea]"
                                data-testid={`booking-row-${b.id}`}
                            >
                                <td className="px-4 py-3 font-mono-ui font-semibold text-[#0A1F44]">
                                    {b.license_plate}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="text-[#0A1F44] font-medium">
                                        {b.customer_name || "—"}
                                    </div>
                                    <div className="text-[#555] text-xs">
                                        {b.customer_type === "business"
                                            ? "Bedrijf"
                                            : "Particulier"}{" "}
                                        · {b.customer_email || "—"}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-[#0A1F44]">
                                    {b.service_name}
                                </td>
                                <td className="px-4 py-3 font-mono-ui text-[#555]">
                                    {b.vat_number || "—"}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-[#0A1F44]">
                                    €
                                    {b.amount_eur
                                        .toFixed(2)
                                        .replace(".", ",")}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => onEdit(b)}
                                        className="inline-flex items-center gap-1 text-[#0A1F44] hover:text-[#ED6A14] transition-colors px-2 py-1"
                                        data-testid={`edit-${b.id}`}
                                        title="Wijzig boeking"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        onClick={() => onDelete(b)}
                                        className="inline-flex items-center gap-1 text-[#555] hover:text-[#D92D20] transition-colors px-2 py-1 ml-1"
                                        data-testid={`delete-${b.id}`}
                                        title="Verwijder boeking"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function EditBookingModal({ booking, days, onClose, onSaved }) {
    const [form, setForm] = useState({
        customer_type: booking.customer_type || "private",
        customer_name: booking.customer_name || "",
        customer_email: booking.customer_email || "",
        vat_number: booking.vat_number || "",
        service_type: booking.service_type,
        date: booking.date,
        slot: booking.slot,
        license_plate: booking.license_plate,
    });
    const [saving, setSaving] = useState(false);
    const isBusiness = form.customer_type === "business";

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                customer_type: form.customer_type,
                customer_name: form.customer_name.trim(),
                customer_email: form.customer_email.trim(),
                vat_number: isBusiness ? form.vat_number.trim() : "",
                service_type: form.service_type,
                date: form.date,
                slot: form.slot,
                license_plate: form.license_plate.trim(),
            };
            await adminApi.put(
                `/admin/bookings/${booking.id}`,
                payload,
            );
            toast.success("Boeking bijgewerkt");
            onSaved();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Opslaan mislukt",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
            data-testid="edit-modal"
            onClick={onClose}
        >
            <div
                className="bg-white border border-[#e5e5e5] w-full max-w-2xl mt-12 mb-12"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[#e5e5e5] px-6 py-4">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-[#555]">
                            Boeking bewerken
                        </p>
                        <h3 className="font-display font-bold text-xl tracking-tight text-[#0A1F44]">
                            {booking.license_plate}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-[#555] hover:text-[#0A1F44] transition-colors"
                        data-testid="edit-modal-close"
                    >
                        <X size={20} />
                    </button>
                </div>
                <form
                    onSubmit={save}
                    className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5"
                    data-testid="edit-form"
                >
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Klant-type
                        </label>
                        <select
                            value={form.customer_type}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    customer_type: e.target.value,
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-customer-type"
                        >
                            <option value="private">Particulier</option>
                            <option value="business">Bedrijf</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            {isBusiness ? "Bedrijfsnaam" : "Naam"}
                        </label>
                        <input
                            value={form.customer_name}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    customer_name: e.target.value,
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-customer-name"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            E-mail
                        </label>
                        <input
                            type="email"
                            value={form.customer_email}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    customer_email: e.target.value.toLowerCase(),
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-customer-email"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Btw-nummer{" "}
                            {isBusiness && (
                                <span className="text-[#ED6A14]">*</span>
                            )}
                        </label>
                        <input
                            value={form.vat_number}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    vat_number: e.target.value.toUpperCase(),
                                })
                            }
                            disabled={!isBusiness}
                            placeholder={isBusiness ? "BE0123.456.789" : "—"}
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44] disabled:opacity-50 uppercase font-display tracking-wider"
                            data-testid="edit-vat"
                        />
                    </div>
                    <div className="md:col-span-2 border-t border-[#e5e5e5] pt-5" />
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Nummerplaat
                        </label>
                        <input
                            value={form.license_plate}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    license_plate: e.target.value.toUpperCase(),
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44] uppercase font-display tracking-wider"
                            data-testid="edit-plate"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Dienst
                        </label>
                        <select
                            value={form.service_type}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    service_type: e.target.value,
                                })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-service"
                        >
                            <option value="exterior">
                                Exterieur (20 min · €24,00)
                            </option>
                            <option value="interior_exterior">
                                Interieur &amp; Exterieur (60 min · €48,00)
                            </option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Datum
                        </label>
                        <select
                            value={form.date}
                            onChange={(e) =>
                                setForm({ ...form, date: e.target.value })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-date"
                        >
                            {/* Always include the booking's current date even if
                                the day was removed afterwards */}
                            {[
                                ...days.map((d) => d.date),
                                ...(days.find((d) => d.date === form.date)
                                    ? []
                                    : [form.date]),
                            ].map((d) => (
                                <option key={d} value={d}>
                                    {new Date(d).toLocaleDateString("nl-BE", {
                                        weekday: "short",
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-1.5">
                            Tijdslot
                        </label>
                        <select
                            value={form.slot}
                            onChange={(e) =>
                                setForm({ ...form, slot: e.target.value })
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 focus:outline-none focus:border-[#ED6A14] text-[#0A1F44]"
                            data-testid="edit-slot"
                        >
                            <option value="morning">
                                Voormiddag (08:00 – 12:00)
                            </option>
                            <option value="afternoon">
                                Namiddag (13:00 – 17:00)
                            </option>
                        </select>
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between border-t border-[#e5e5e5] pt-5 mt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-sm text-[#555] hover:text-[#0A1F44] transition-colors font-semibold"
                            data-testid="edit-cancel-btn"
                        >
                            Annuleren
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-50"
                            data-testid="edit-save-btn"
                        >
                            {saving ? (
                                <Loader2 className="animate-spin" size={16} />
                            ) : (
                                <Save size={16} />
                            )}{" "}
                            Wijzigingen opslaan
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function groupBookings(list) {
    const byDate = {};
    for (const b of list) {
        if (!byDate[b.date]) {
            byDate[b.date] = { date: b.date, morning: [], afternoon: [] };
        }
        if (b.slot === "morning") byDate[b.date].morning.push(b);
        else byDate[b.date].afternoon.push(b);
    }
    return Object.values(byDate)
        .map((d) => ({
            ...d,
            total: d.morning.length + d.afternoon.length,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

function formatDayHeading(isoDate) {
    const d = new Date(isoDate);
    return d.toLocaleDateString("nl-BE", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

function serviceDuration(serviceType) {
    return serviceType === "interior_exterior" ? 45 : 15;
}

function EditDayModal({ day, onClose, onSave }) {
    const [workers, setWorkers] = useState(day.workers_count || 2);
    const [mode, setMode] = useState(day.slot_mode || "full");
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        await onSave({ workers_count: workers, slot_mode: mode });
        setSaving(false);
    };
    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            data-testid="edit-day-modal"
        >
            <form
                onSubmit={submit}
                className="bg-white max-w-md w-full p-6"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-bold text-xl text-[#0A1F44]">
                        {new Date(day.date).toLocaleDateString("nl-BE", {
                            weekday: "long",
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                        })}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#555] hover:text-[#0A1F44]"
                        data-testid="edit-day-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                            Poetsers (1–4)
                        </label>
                        <select
                            value={workers}
                            onChange={(e) =>
                                setWorkers(parseInt(e.target.value, 10))
                            }
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 text-[#0A1F44]"
                            data-testid="edit-day-workers"
                        >
                            {[1, 2, 3, 4].map((n) => (
                                <option key={n} value={n}>
                                    {n} poetser{n === 1 ? "" : "s"} · {n * 480} min/dag
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                            Dagdeel
                        </label>
                        <select
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            className="w-full border-2 border-[#e5e5e5] bg-transparent py-2.5 px-3 text-[#0A1F44]"
                            data-testid="edit-day-mode"
                        >
                            <option value="full">Volledige dag (VM + NM)</option>
                            <option value="morning">Enkel voormiddag</option>
                            <option value="afternoon">Enkel namiddag</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#555] hover:text-[#0A1F44] px-4 py-2 text-sm"
                    >
                        Annuleren
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 bg-[#ED6A14] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C9550C] transition-colors disabled:opacity-60"
                        data-testid="edit-day-save"
                    >
                        {saving ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Save size={14} />
                        )}
                        Opslaan
                    </button>
                </div>
            </form>
        </div>
    );
}

function Kpi({ label, value, testId }) {
    return (
        <div className="bg-white p-6" data-testid={testId}>
            <p className="text-xs uppercase tracking-widest text-[#555]">
                {label}
            </p>
            <p className="font-display font-bold text-4xl tracking-tighter mt-3 text-[#0A1F44]">
                {value}
            </p>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", step, min, max, testId }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-[#555] block mb-2">
                {label}
            </label>
            <input
                type={type}
                step={step}
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border-2 border-[#e5e5e5] bg-transparent py-3 px-4 text-base focus:outline-none focus:border-[#ED6A14] transition-colors text-[#0A1F44]"
                data-testid={testId}
            />
        </div>
    );
}

function StatusBadge({ status, paymentStatus }) {
    let label = status;
    let color = "bg-[#eaeaea] text-[#121212]";
    if (status === "confirmed") {
        label = paymentStatus === "paid" ? "Betaald" : "Bevestigd";
        color =
            paymentStatus === "paid"
                ? "bg-[#00C853]/15 text-[#0A8C3F]"
                : "bg-[#ED6A14]/10 text-[#ED6A14]";
    } else if (status === "reserved") {
        label = "Wacht op bevestiging";
        color = "bg-[#0A1F44]/10 text-[#0A1F44]";
    } else if (status === "cancelled") {
        label = "Geannuleerd";
        color = "bg-[#D92D20]/10 text-[#D92D20]";
    }
    return (
        <span
            className={`inline-block px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${color}`}
        >
            {label}
        </span>
    );
}
